import { useQuery } from "@apollo/client";
import { Stack } from "@mui/material";
import { gql } from "generated-graphql";
import { ProgramArea, SubmissionStepType } from "generated-graphql/graphql";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useMemo } from "react";
import { FacilityContacts } from "routes/Customer/Contacts/FacilityContacts";
import { isRcraContactIssue } from "util/facilityContacts";

import { StepHeader } from "../StepHeader";
import { StepContinueFooter } from "./StepContinueFooter";

const GET_STEPPER_FACILITY_CONTACTS = gql(`
  query GetStepperFacilityContacts($facilityId: ID!, $programArea: ProgramArea) {
    facility(id: $facilityId) {
      id
      state
      ...facilityContacts
      issues {
        ...issue
      }
    }
  }
`);

/**
 * Contacts stepper step. Renders the same RCRA-scoped facility-contact view as
 * the waste contacts page (`WasteContacts`): contacts are filtered to the RCRA
 * program area by the query, and compliance issues are filtered to RCRA contact
 * issues via `isRcraContactIssue`. Touches the `CONTACTS` step on continue.
 */
export function ContactsStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const { canEdit } = useWasteStepper();
  const { data, previousData } = useQuery(GET_STEPPER_FACILITY_CONTACTS, {
    variables: {
      facilityId,
      programArea: ProgramArea.Rcra,
    },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });
  const issues = useMemo(() => data?.facility?.issues ?? [], [data?.facility?.issues]);

  return (
    <Stack spacing={2}>
      <StepHeader title="Contacts" />
      <FacilityContacts
        // The stepper owns editability (staff-only, unfrozen) — without this,
        // FacilityContacts falls back to facility-contact write permission and
        // a customer reviewer could edit contacts from a read-only step.
        isReadOnly={!canEdit}
        loading={!data && !previousData}
        facilityId={data?.facility?.id ?? ""}
        facilityState={data?.facility?.state ?? ""}
        facilityContacts={data?.facility?.contacts ?? []}
        getRowIssues={(row) =>
          issues.filter(
            (issue) =>
              ((issue.modelId === row.person.id && issue.modelName === "Person") ||
                (issue.modelName === "FacilityContact" && issue.modelId === row.id)) &&
              isRcraContactIssue(issue)
          )
        }
        facilityContactIssues={issues.filter(
          (issue) => issue.key === "contacts" && isRcraContactIssue(issue)
        )}
        refetchQueries={[GET_STEPPER_FACILITY_CONTACTS]}
        addContactButtonVariant="text"
      />
      <StepContinueFooter submissionId={submissionId} step={SubmissionStepType.Contacts} />
    </Stack>
  );
}
